/**
 * The three doors' vocabulary, shared by the search page, the result cards,
 * the Radar page and the tour. Values mirror backend/audience.py and
 * backend/sector.py; a new value is added in both places or not at all.
 */
export const AUDIENCE_LABELS = {
  entry: "Entry level",
  intern: "Internship",
  apprentice: "Apprenticeship",
  seasonal: "Seasonal",
  part_time: "Part-time",
  student: "Students",
};

/** "Looking for" menu: one choice that may fan out to several tags. */
export const LOOKING_FOR = [
  { value: "all", label: "All roles", audience: [] },
  { value: "entry", label: "Entry level — no experience required", audience: ["entry"] },
  { value: "intern", label: "Internships", audience: ["intern"] },
  { value: "apprentice", label: "Apprenticeships", audience: ["apprentice"] },
  { value: "seasonal", label: "Seasonal & part-time", audience: ["seasonal", "part_time"] },
  { value: "student", label: "Student roles", audience: ["student"] },
];

/** "Sector" menu: private, education, or government at a level. */
export const SECTOR_OPTIONS = [
  { value: "all", label: "Companies and government", sector: "", gov_level: "" },
  { value: "private", label: "Companies", sector: "private", gov_level: "" },
  { value: "government", label: "Government — all levels", sector: "government", gov_level: "" },
  { value: "federal", label: "Federal", sector: "government", gov_level: "federal" },
  { value: "state", label: "State", sector: "government", gov_level: "state" },
  { value: "local", label: "City & county", sector: "government", gov_level: "local" },
  { value: "education", label: "Schools & universities", sector: "education", gov_level: "" },
];

export const AGE_OPTIONS = [
  { value: "any", label: "Any age" },
  { value: "14", label: "Open to 14+" },
  { value: "16", label: "Open to 16+" },
  { value: "18", label: "Open to 18+" },
];

export const GOV_LEVEL_LABELS = { federal: "Federal", state: "State", local: "City & county" };

export function sectorLabel(job) {
  if (!job) return "";
  if (job.sector === "government") return GOV_LEVEL_LABELS[job.gov_level] || "Government";
  if (job.sector === "education") return "Education";
  return "";
}

/** Sector and level from a menu value, and back. */
export function sectorParams(value) {
  const o = SECTOR_OPTIONS.find((x) => x.value === value) || SECTOR_OPTIONS[0];
  return { sector: o.sector, gov_level: o.gov_level };
}
/** One value or several: the tags they fan out to, without repeats. */
export function audienceParams(value) {
  const values = Array.isArray(value) ? value : [value];
  const out = [];
  values.forEach((v) => {
    const o = LOOKING_FOR.find((x) => x.value === v);
    (o ? o.audience : []).forEach((t) => { if (!out.includes(t)) out.push(t); });
  });
  return out;
}

/**
 * The three doors as a mode on the profile. Mirrors backend/modes.py; a
 * backend test holds the two lists to each other. `blurb` is the line under
 * the name on the onboarding and profile cards; `defaults` is what the
 * search page starts with for someone who has not touched the criteria.
 */
export const MODES = [
  {
    value: "professional",
    label: "Professional",
    blurb: "Company boards, sponsorship stances, tailored resumes for each posting.",
    defaults: { lookingFor: [], sectorChoice: "all", ageChoice: "any" },
  },
  {
    value: "early",
    label: "Early start",
    blurb: "First jobs, internships, apprenticeships and seasonal work — from any board.",
    defaults: { lookingFor: [], sectorChoice: "all", ageChoice: "any" },
  },
  {
    value: "government",
    label: "Government",
    blurb: "Federal, state and local postings with closing dates and pay grades.",
    defaults: { lookingFor: [], sectorChoice: "government", ageChoice: "any" },
  },
];

/** Early mode only; a bracket, never a birth date. */
export const AGE_BRACKETS = [
  { value: "under_16", label: "Under 16", ageChoice: "14" },
  { value: "16_17", label: "16 or 17", ageChoice: "16" },
  { value: "18_plus", label: "18 or older", ageChoice: "any" },
];

export const DEFAULT_MODE = "professional";

export function modeOf(profile) {
  const v = profile?.mode;
  return MODES.some((m) => m.value === v) ? v : DEFAULT_MODE;
}

export function isMinor(profile) {
  return modeOf(profile) === "early" && ["under_16", "16_17"].includes(profile?.age_bracket);
}

/** The search page's starting criteria for this profile. */
export function modeDefaults(profile) {
  const mode = MODES.find((m) => m.value === modeOf(profile)) || MODES[0];
  const out = { ...mode.defaults };
  if (mode.value === "early") {
    const b = AGE_BRACKETS.find((x) => x.value === profile?.age_bracket);
    if (b) out.ageChoice = b.ageChoice;
  }
  return out;
}

/**
 * What the sidebar says in each mode. Only the words change: routes and
 * test ids are the same in every door, so nothing else has to know.
 */
export const MODE_NAV = {
  professional: {
    tagline: "AI job command center",
    headings: {},
    labels: {},
  },
  early: {
    tagline: "Your first-job guide",
    headings: { "Find work": "Find a first job" },
    labels: { "/resumes": "My resume", "/stars": "My stories" },
  },
  government: {
    tagline: "Public service job center",
    headings: { "Find work": "Find public service" },
    // The page is still titled Job Search; a nav label that disagrees with
    // the title it leads to is a small lie every visit.
    labels: {},
  },
};

export function modeNav(profile) {
  return MODE_NAV[modeOf(profile)] || MODE_NAV.professional;
}

/**
 * The handoff from a door's landing page to onboarding. /signup?door=early
 * leaves a note; onboarding reads it once and opens on that door. Session
 * scoped, so a refresh keeps it and a later visit does not.
 */
export const DOOR_KEY = "nextopp.door";

export function rememberDoor(value) {
  if (!MODES.some((m) => m.value === value)) return;
  try { window.sessionStorage.setItem(DOOR_KEY, value); } catch {}
}

export function takeDoor() {
  try {
    const v = window.sessionStorage.getItem(DOOR_KEY);
    window.sessionStorage.removeItem(DOOR_KEY);
    return MODES.some((m) => m.value === v) ? v : null;
  } catch { return null; }
}

/** Audience tags on a request (or a posting) → the "looking for" choices that ask for them. */
export function choicesForAudience(tags) {
  const out = [];
  (tags || []).forEach((t) => {
    const o = LOOKING_FOR.find((x) => x.value !== "all" && x.audience.includes(t));
    if (o && !out.includes(o.value)) out.push(o.value);
  });
  return out;
}
